import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Paper, Popover, Theme, Typography } from '@mui/material';
import Email from '@mui/icons-material/Email';
import makeStyles from '@mui/styles/makeStyles';
import Phone from '@mui/icons-material/Phone';
import {
  ContactSchema,
  ConversationThreadMentions,
} from '../../definitions/schemas';
import { getDisplayName } from '../../lib/DisplayName';
import { formatPhoneNumber } from '../../lib/PhoneNumber';
import { buildConversationMessageClassName } from '../mentions/helper';
import { DEFAULT_ROOM_MESSAGE_ID } from './Message';
import {
  DSR_MENTION_SELECTOR,
  MENTION_ATTRIBUTES,
  MOUSE_LEAVE_LISTENER,
  MOUSE_OVER_LISTENER,
} from '../Input/helper';

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    backgroundColor: 'var(--avatar-background-default)',
    color: theme.palette.text.primary,
    fontSize: theme.typography.body2.fontSize,
    height: theme.spacing(5),
    width: theme.spacing(5),
  },
  basicInformation: {
    [theme.breakpoints.only('md')]: {
      flexDirection: 'column',
    },
  },
  cardContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    height: '100%',
    margin: 'auto',
    padding: theme.spacing(2, 2, 1, 2),
    position: 'relative',
    width: theme.spacing(40),
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  informationData: {
    ...theme.typography.body2,
    color: 'var(--divider-color-light)',
    wordBreak: 'break-word',
  },
  informationIcon: {
    color: 'var(--gray-lighter)',
  },
  name: {
    wordBreak: 'break-word',
  },
  nameWrapper: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    [theme.breakpoints.only('md')]: {
      margin: 0,
      marginTop: theme.spacing(2),
    },
  },
}));

interface Props {
  mentions?: ConversationThreadMentions[];
  messageId?: string;
}

const MessageCard: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();

  const { mentions, messageId = DEFAULT_ROOM_MESSAGE_ID } = props;

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [open, setOpen] = useState(false);

  const mention = useRef<ConversationThreadMentions | undefined>();

  useEffect(() => {
    // This function will be executed after the component renders

    // Attach event handlers to elements with specific classes or other selectors
    const elementsWithEvents = document.querySelectorAll(
      `.${buildConversationMessageClassName(messageId)} ${DSR_MENTION_SELECTOR}`
    );

    elementsWithEvents.forEach((element) => {
      // Add your event listener here
      element.addEventListener(MOUSE_OVER_LISTENER, onMentionHovered);
      element.addEventListener(MOUSE_LEAVE_LISTENER, onNotHover);
    });

    return () => {
      elementsWithEvents.forEach((element) => {
        element.removeEventListener(MOUSE_OVER_LISTENER, onMentionHovered);
        element.removeEventListener(MOUSE_LEAVE_LISTENER, onMentionHovered);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const onMentionHovered = (e: any) => {
    const mentionAnchor = e.target as HTMLAnchorElement;

    const id = e.target.getAttribute(MENTION_ATTRIBUTES.ENTITY_ID);
    const type = e.target.getAttribute(MENTION_ATTRIBUTES.ENTITY_TYPE);

    const entity = mentions?.find(
      (mention) => mention.entityId === id && mention.entityType === type
    );

    if (!entity) {
      return;
    }

    const displayName = getDisplayName({
      email: entity.email,
      firstName: entity.firstName,
      lastName: entity.lastName,
    } as ContactSchema);

    if (displayName === '') {
      return;
    }

    mention.current = { displayName, ...entity };
    setAnchorEl(mentionAnchor);
    setOpen(true);
  };

  const onNotHover = () => {
    mention.current = undefined;
    setAnchorEl(null);
    setOpen(false);
  };

  if (!anchorEl || mention.current === undefined) {
    return <></>;
  }

  const renderPhone = () => {
    const phone =
      formatPhoneNumber(mention.current?.mobilePhone) ||
      formatPhoneNumber(mention.current?.phoneNumber);

    if (phone === '') {
      return <></>;
    }

    return (
      <Box alignItems="center" display="flex" mb={2} width="100%">
        <Phone className={classes.informationIcon} />
        <Box className={classes.informationData} ml={1.5}>
          <Typography variant="body2">{phone}</Typography>
        </Box>
      </Box>
    );
  };

  const renderEmail = () => {
    if (mention.current?.email === undefined) {
      return <></>;
    }

    return (
      <Box alignItems="center" display="flex" mb={2} width="100%">
        <Email className={classes.informationIcon} />
        <Box className={classes.informationData} ml={1.5}>
          <Typography variant="body2">{mention.current?.email}</Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      disablePortal
      disableRestoreFocus
      id="mouse-over-popover"
      onClose={onNotHover}
      onMouseLeave={onNotHover}
      open={open}
      sx={{
        pointerEvents: 'none',
      }}
      transformOrigin={{
        horizontal: 'left',
        vertical: 'top',
      }}
    >
      <Paper>
        <Box
          className={classes.cardContainer}
          onClick={onNotHover}
          width="100%"
        >
          <Box
            className={classes.basicInformation}
            display="flex"
            position="relative"
            width="100%"
          >
            <Avatar
              classes={{
                root: classes.avatar,
              }}
              src={mention.current?.avatarUri || ''}
            >
              {(mention.current?.displayName || '-')[0].toUpperCase()}
            </Avatar>
            <Box
              className={classes.nameWrapper}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <Box className={classes.name} component="strong">
                <Typography variant="body2">
                  {mention.current?.displayName}
                </Typography>
              </Box>
              <Box className={classes.informationData} mt={0.5}>
                <Typography variant="body2">
                  {mention.current?.jobTitle || ''}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box mt={3}>
            {renderPhone()}
            {renderEmail()}
          </Box>
        </Box>
      </Paper>
    </Popover>
  );
};

export default MessageCard;
